import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { useAuthStore, useUIStore } from './store';
import { Spinner } from './components/ui';

// Lazy load pages for code splitting
const LoginPage = lazy(() => import('./pages/LoginPage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const MainLayout = lazy(() => import('./layouts/MainLayout'));

// Placeholder pages (to be implemented)
const BookingsPage = lazy(() => import('./pages/BookingsPage'));
const BookingDetailPage = lazy(() => import('./pages/BookingDetailPage'));
const NewBookingPage = lazy(() => import('./pages/NewBookingPage'));
const ApprovalsPage = lazy(() => import('./pages/ApprovalsPage'));
const RoomsPage = lazy(() => import('./pages/RoomsPage'));
const GuestsPage = lazy(() => import('./pages/GuestsPage'));
const HostelsPage = lazy(() => import('./pages/HostelsPage'));
const HostelDetailPage = lazy(() => import('./pages/HostelDetailPage'));
const HostelEditPage = lazy(() => import('./pages/HostelEditPage'));
const PublicBookingPage = lazy(() => import('./pages/PublicBookingPage'));
const BookingStatusPage = lazy(() => import('./pages/BookingStatusPage'));
const PaymentCheckoutPage = lazy(() => import('./pages/PaymentCheckoutPage'));
const ApprovalActionPage = lazy(() => import('./pages/ApprovalActionPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const BulkBookingsPage = lazy(() => import('./pages/BulkBookingsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const UserSettingsPage = lazy(() => import('./pages/UserSettingsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AmenitiesPage = lazy(() => import('./pages/AmenitiesPage'));
const HierarchyPage = lazy(() => import('./pages/HierarchyPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));

// SuperAdmin pages
const SuperAdminDashboardPage = lazy(() => import('./pages/SuperAdminDashboardPage'));

// Admin pages
const ApprovalConfigPage = lazy(() => import('./pages/admin/ApprovalConfigPage'));
const TenantInfoPage = lazy(() => import('./pages/admin/TenantInfoPage'));
const EmployeesPage = lazy(() => import('./pages/admin/EmployeesPage'));
const EmployeeDetailPage = lazy(() => import('./pages/admin/EmployeeDetailPage'));
const LandingPageCustomizationPage = lazy(() => import('./pages/admin/LandingPageCustomizationPage'));
const AuditLogPage = lazy(() => import('./pages/admin/AuditLogPage'));

// Query Client configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Loading fallback component
const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <Spinner size="lg" />
      <p className="mt-4 text-neutral-500">Loading...</p>
    </div>
  </div>
);

// Protected Route wrapper
interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, isLoading, accessToken, logout } = useAuthStore();
  const location = useLocation();

  // Check for corrupted auth state: isAuthenticated but no token
  const hasToken = accessToken || localStorage.getItem('accessToken');
  const isCorruptedState = isAuthenticated && !hasToken;

  // Handle corrupted auth state in useEffect to avoid setState during render
  useEffect(() => {
    if (isCorruptedState) {
      logout();
    }
  }, [isCorruptedState, logout]);

  if (isLoading) {
    return <PageLoader />;
  }

  // If auth state is corrupted, redirect to login
  if (isCorruptedState) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // SuperAdmin should be redirected to /superadmin if they try to access /dashboard
  const isSuperAdmin = user?.roles?.some((role: any) => 
    typeof role === 'string' ? role === 'SUPER_ADMIN' : role?.code === 'SUPER_ADMIN'
  );
  if (isSuperAdmin && location.pathname === '/dashboard') {
    return <Navigate to="/superadmin" replace />;
  }

  if (allowedRoles && allowedRoles.length > 0) {
    // Handle both string roles and role objects with code property
    const hasAccess = user?.roles?.some((role: any) => 
      typeof role === 'string' 
        ? allowedRoles.includes(role) 
        : allowedRoles.includes(role?.code)
    );
    if (!hasAccess) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};

// Public Route wrapper (redirects authenticated users)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return <PageLoader />;
  }

  if (isAuthenticated) {
    // SuperAdmin goes to /superadmin, others go to /dashboard or their previous location
    const isSuperAdmin = user?.roles?.some((role: any) => 
      typeof role === 'string' ? role === 'SUPER_ADMIN' : role?.code === 'SUPER_ADMIN'
    );
    const defaultRedirect = isSuperAdmin ? '/superadmin' : '/dashboard';
    const from = (location.state as { from?: { pathname: string } })?.from?.pathname || defaultRedirect;
    return <Navigate to={from} replace />;
  }

  return <>{children}</>;
};

// Theme initializer component
const ThemeInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { theme } = useUIStore();

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');

    if (theme === 'system') {
      // Default to light for system preference
      root.classList.add('light');
    } else {
      root.classList.add(theme);
    }
  }, [theme]);

  return <>{children}</>;
};

// Scroll to top on route change
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Main App component
const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeInitializer>
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/newbooking" element={<PublicBookingPage />} />
              <Route path="/booking-status" element={<BookingStatusPage />} />
              <Route path="/pay/:token" element={<PaymentCheckoutPage />} />
              <Route path="/approve-booking/:token" element={<ApprovalActionPage />} />
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <LoginPage />
                  </PublicRoute>
                }
              />

              {/* Protected routes */}
              <Route
                element={
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/bookings" element={<BookingsPage />} />
                <Route path="/bookings/new" element={<NewBookingPage />} />
                <Route path="/bookings/bulk" element={<BulkBookingsPage />} />
                <Route path="/bookings/:id" element={<BookingDetailPage />} />
                <Route path="/approvals" element={<ApprovalsPage />} />
                <Route path="/rooms" element={<RoomsPage />} />
                <Route path="/guests" element={<GuestsPage />} />
                <Route path="/hostels" element={<HostelsPage />} />
                <Route path="/hostels/:id" element={<HostelDetailPage />} />
                <Route path="/hostels/new" element={<HostelEditPage />} />
                <Route path="/hostels/:id/edit" element={<HostelEditPage />} />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/amenities" element={<AmenitiesPage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route
                  path="/users"
                  element={<Navigate to="/admin/employees" replace />}
                />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/user-settings" element={<UserSettingsPage />} />
                <Route path="/profile" element={<ProfilePage />} />

                {/* Admin configuration routes */}
                <Route
                  path="/admin/tenant"
                  element={
                    <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'TENANT_ADMIN']}>
                      <TenantInfoPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/employees"
                  element={
                    <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'HOSTEL_ADMIN']}>
                      <EmployeesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/employees/:id"
                  element={
                    <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'HOSTEL_ADMIN']}>
                      <EmployeeDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hierarchy"
                  element={
                    <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'HOSTEL_ADMIN', 'CONVENER']}>
                      <HierarchyPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/approvalflow"
                  element={
                    <ProtectedRoute allowedRoles={['TENANT_ADMIN']}>
                      <ApprovalConfigPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/landing-page"
                  element={
                    <ProtectedRoute allowedRoles={['TENANT_ADMIN']}>
                      <LandingPageCustomizationPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/audit-log"
                  element={
                    <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'TENANT_ADMIN']}>
                      <AuditLogPage />
                    </ProtectedRoute>
                  }
                />

                {/* SuperAdmin routes */}
                <Route
                  path="/superadmin"
                  element={
                    <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
                      <SuperAdminDashboardPage />
                    </ProtectedRoute>
                  }
                />
              </Route>

              {/* Catch all - redirect to dashboard or login */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Suspense>

          {/* Toast notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#fff',
                color: '#1e293b',
                boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                borderRadius: '12px',
                padding: '16px',
              },
              success: {
                iconTheme: {
                  primary: '#16a34a',
                  secondary: '#fff',
                },
              },
              error: {
                iconTheme: {
                  primary: '#dc2626',
                  secondary: '#fff',
                },
              },
            }}
          />
        </ThemeInitializer>
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
